import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  MessageCircle, CheckSquare, Calendar, FileText, Users, 
  Home, Plus, Settings, AlertTriangle, CheckCircle, Clock,
  ArrowLeft, Target, Heart, Star, Trash2, Archive, 
  Eye, EyeOff, Crown, Brain, Send
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import PersistentFooter from './PersistentFooter';
import UniversalHeartToggle from './UniversalHeartToggle';
import ProjectCalendar from './ProjectCalendar';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyProjectsPage = ({ player, onNavigateHome, onNavigateToGame }) => {
  const [projects, setProjects] = useState([]);
  const [activeView, setActiveView] = useState('overview'); // overview, messages, todo, calendar, docs, peeps
  const [selectedProject, setSelectedProject] = useState(null);
  const [spiralPhase, setSpiralPhase] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Project salon states
  const [currentMessage, setCurrentMessage] = useState('');
  const [projectMessages, setProjectMessages] = useState([]);
  const [todos, setTodos] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [newTodoText, setNewTodoText] = useState('');

  // Grant spiral animation
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.15) % 360);
    }, 50);
    return () => clearInterval(spiralInterval);
  }, []);

  // Load user's projects
  useEffect(() => {
    loadUserProjects();
  }, []);

  const loadUserProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('planetary_fed_token');
      const response = await axios.get(`${API}/projects/my-projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Projects load error:', error);
      // Enhanced mock data with thumbnails
      setProjects([
        {
          id: '1',
          name: 'Buy Mom a Car',
          type: 'family',
          health_status: 'attention_needed',
          members: 4,
          last_activity: '2 hours ago',
          progress: 65,
          due_date: '2025-10-01',
          priority: 'high',
          overdue_todos: 2,
          pending_votes: 1,
          total_todos: 8,
          completed_todos: 5,
          thumbnail: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=200&h=200&fit=crop&crop=center',
          description: 'Family collaboration to find the perfect car for mom'
        },
        {
          id: '2', 
          name: 'Community Garden Setup',
          type: 'community',
          health_status: 'healthy',
          members: 8,
          last_activity: '30 minutes ago',
          progress: 80,
          due_date: '2025-11-15',
          priority: 'medium',
          overdue_todos: 0,
          pending_votes: 0,
          total_todos: 12,
          completed_todos: 10,
          thumbnail: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=200&h=200&fit=crop&crop=center',
          description: 'Regenerative community garden project with consciousness-driven collaboration'
        },
        {
          id: '3',
          name: 'Personal Wellness Journey',
          type: 'personal',
          health_status: 'excellent',
          members: 1,
          last_activity: '1 day ago',
          progress: 90,
          due_date: null,
          priority: 'low',
          overdue_todos: 0,
          pending_votes: 0,
          total_todos: 5,
          completed_todos: 4,
          thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=200&h=200&fit=crop&crop=center',
          description: 'Individual consciousness expansion and wellness practices'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const convertMessageToTodo = (messageText) => {
    // Enhanced Apple Reminders style todo creation
    const priority = messageText.toLowerCase().includes('urgent') || messageText.toLowerCase().includes('asap') ? 1 :
                    messageText.toLowerCase().includes('important') ? 2 :
                    messageText.toLowerCase().includes('later') || messageText.toLowerCase().includes('someday') ? 4 : 3;
    
    // Extract due date if mentioned
    let dueDate = null;
    const dueDateRegex = /(by|due|before)\s+(\w+\s+\d+|\d+\/\d+|\d+\-\d+)/i;
    const match = messageText.match(dueDateRegex);
    if (match) {
      try {
        dueDate = new Date(match[2]).toISOString();
      } catch (e) {
        // Default to one week if date parsing fails
        dueDate = new Date(Date.now() + 7*24*60*60*1000).toISOString();
      }
    }
    
    const newTodo = {
      id: Date.now().toString(),
      text: messageText,
      completed: false,
      due_date: dueDate,
      priority: priority,
      assigned_to: player.id,
      created_from_message: true,
      created_at: new Date().toISOString(),
      notes: "",
      project_id: selectedProject?.id || "general"
    };
    
    setTodos(prev => [newTodo, ...prev]);
    
    // Enhanced confirmation with Apple Reminders style
    const confirmMessage = {
      id: 'todo_created_' + Date.now(),
      type: 'system',
      content: `✅ Task added to ${selectedProject?.name || 'your list'}: "${messageText.substring(0, 40)}${messageText.length > 40 ? '...' : ''}" ${priority === 1 ? '(High Priority)' : priority === 2 ? '(Important)' : ''}`,
      sender: 'M.ai.ry Assistant',
      timestamp: new Date(),
      avatar: {
        name: 'M.ai.ry',
        image: 'https://api.dicebear.com/7.x/bottts/svg?seed=assistant&backgroundColor=22c55e'
      }
    };
    
    setProjectMessages(prev => [...prev, confirmMessage]);
  };

  const sendProjectMessage = async () => {
    if (!currentMessage.trim()) return;

    const userMessage = {
      id: 'msg_' + Date.now(),
      content: currentMessage,
      sender: player.name,
      timestamp: new Date(),
      project_id: selectedProject?.id || "general",
      type: 'user_message'
    };
    
    setProjectMessages(prev => [...prev, userMessage]);
    
    // Enhanced smart todo detection - Apple Reminders style intelligence
    const todoPatterns = [
      /need to\s+(.+)/i,
      /should\s+(.+)/i,
      /must\s+(.+)/i,
      /have to\s+(.+)/i,
      /remember to\s+(.+)/i,
      /don't forget to\s+(.+)/i,
      /make sure to\s+(.+)/i,
      /by\s+\w+\s+we\s+need\s+(.+)/i,
      /deadline\s+(.+)/i,
      /due\s+(.+)/i,
      /task:\s*(.+)/i,
      /todo:\s*(.+)/i
    ];
    
    const messageText = currentMessage.toLowerCase();
    const isTodoLike = todoPatterns.some(pattern => pattern.test(messageText)) ||
                      todoPatterns.some(keyword => messageText.includes(keyword));
    
    if (isTodoLike) {
      // Enhanced todo offer with context
      setTimeout(() => {
        const todoOfferMessage = {
          id: 'todo_offer_' + Date.now(),
          type: 'todo_offer',
          content: `💡 I detected a task in your message. Would you like me to add it to your Apple Reminders-style todo list?`,
          sender: 'M.ai.ry Assistant',
          timestamp: new Date(),
          original_message: currentMessage,
          convert_to_todo: true,
          avatar: {
            name: 'M.ai.ry',
            image: 'https://api.dicebear.com/7.x/bottts/svg?seed=assistant&backgroundColor=eab308'
          }
        };
        
        setProjectMessages(prev => [...prev, todoOfferMessage]);
      }, 1500);
    }
    
    setCurrentMessage('');
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'excellent': return 'text-green-400 bg-green-500/20 border-green-400/50';
      case 'healthy': return 'text-blue-400 bg-blue-500/20 border-blue-400/50';
      case 'attention_needed': return 'text-yellow-400 bg-yellow-500/20 border-yellow-400/50';
      case 'critical': return 'text-red-400 bg-red-500/20 border-red-400/50';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-400/50';
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'excellent': return <Star className="w-4 h-4" />;
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'attention_needed': return <Clock className="w-4 h-4" />;
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      default: return <Target className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{
             backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
             backgroundSize: 'cover',
             backgroundPosition: 'center center'
           }}>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center space-y-4">
          <div 
            className="w-16 h-16 mx-auto mb-4"
            style={{ transform: `rotate(${spiralPhase}deg)` }}
          >
            <GrantitudeSpiral size={64} />
          </div>
          <div 
            className="text-white text-xl font-medium"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            Loading your Project Salons...
          </div>
        </div>
      </div>
    );
  }

  // Project overview (list view)
  if (!selectedProject) {
    return (
      <div 
        className="min-h-screen relative"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center'
        }}
      >
        {/* Grant spiral background */}
        <div className="fixed inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
          <div 
            className="absolute inset-0 flex items-center justify-center"
            style={{
              transform: `scale(8) rotate(${spiralPhase}deg)`,
              filter: 'blur(6px) saturate(1.8) brightness(1.4)',
              mixBlendMode: 'overlay'
            }}
          >
            <GrantitudeSpiral size={400} />
          </div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70"></div>

        <div className="relative z-10 max-w-6xl mx-auto p-6 pb-24">
          
          {/* Header */}
          <div className="flex items-center justify-between mb-8 pt-8">
            <Button
              onClick={onNavigateToGame}
              variant="outline"
              className="text-white border-white/40 hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Game
            </Button>
            
            <div className="text-center">
              <h1 
                className="text-4xl font-bold text-white"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 25px rgba(255, 255, 255, 0.4)'
                }}
              >
                My Project Salons
              </h1>
              <p 
                className="text-cyan-200 text-lg"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Consciousness-driven collaboration
              </p>
            </div>
            
            <Button
              onClick={() => alert('Create new project salon coming soon...')}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </div>

          {/* Projects Grid - Enhanced with Health Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => setSelectedProject(project)}
                className="group text-left"
              >
                <Card className="bg-black/40 backdrop-blur-xl border border-white/20 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <CardContent className="p-6">
                    
                    {/* Project Thumbnail & Header */}
                    <div className="flex items-start space-x-4 mb-4">
                      {/* Project Thumbnail */}
                      <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-white/20 shrink-0">
                        <img 
                          src={project.thumbnail}
                          alt={project.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to generated avatar if thumbnail fails
                            e.target.src = `https://api.dicebear.com/7.x/shapes/svg?seed=${project.name}&backgroundColor=4338ca`;
                          }}
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="text-white text-lg font-bold mb-2 truncate"
                          style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                        >
                          {project.name}
                        </h3>
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge 
                            className={`text-xs ${getHealthColor(project.health_status)}`}
                          >
                            {getHealthIcon(project.health_status)}
                            <span className="ml-1 capitalize">{project.health_status.replace('_', ' ')}</span>
                          </Badge>
                          <Badge variant="outline" className="text-xs text-purple-300 border-purple-400">
                            {project.type}
                          </Badge>
                        </div>
                        <p className="text-white/60 text-xs leading-tight">
                          {project.description}
                        </p>
                      </div>
                    </div>
                    
                    {/* Progress Bar with Enhanced Details */}
                    <div className="mb-4">
                      <div className="flex justify-between text-sm text-white/70 mb-1">
                        <span>Progress</span>
                        <span>{project.progress}% • {project.completed_todos}/{project.total_todos} tasks</span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-3">
                        <div 
                          className={`h-3 rounded-full transition-all duration-1000 ${
                            project.health_status === 'excellent' ? 'bg-gradient-to-r from-green-400 to-emerald-400' :
                            project.health_status === 'healthy' ? 'bg-gradient-to-r from-blue-400 to-cyan-400' :
                            project.health_status === 'attention_needed' ? 'bg-gradient-to-r from-yellow-400 to-orange-400' :
                            'bg-gradient-to-r from-red-400 to-pink-400'
                          }`}
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Project Stats - Enhanced for Simplicity */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-white/70 flex items-center">
                          <Users className="w-3 h-3 mr-1" />
                          Team
                        </span>
                        <span className="text-cyan-300">{project.members} people</span>
                      </div>
                      
                      {project.due_date && (
                        <div className="flex items-center justify-between">
                          <span className="text-white/70 flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            Due
                          </span>
                          <span className={`${
                            new Date(project.due_date) < new Date() ? 'text-red-300' : 
                            new Date(project.due_date) < new Date(Date.now() + 7*24*60*60*1000) ? 'text-yellow-300' :
                            'text-green-300'
                          }`}>
                            {new Date(project.due_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <span className="text-white/70">Last Update</span>
                        <span className="text-green-300">{project.last_activity}</span>
                      </div>
                      
                      {/* Critical Health Indicators */}
                      {project.overdue_todos > 0 && (
                        <div className="bg-red-500/20 rounded-lg p-2 border border-red-400/30">
                          <div className="flex items-center justify-between">
                            <span className="text-red-300 flex items-center text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Needs Attention
                            </span>
                            <span className="text-red-300 text-xs font-bold">{project.overdue_todos} overdue</span>
                          </div>
                        </div>
                      )}
                      
                      {project.pending_votes > 0 && (
                        <div className="bg-purple-500/20 rounded-lg p-2 border border-purple-400/30">
                          <div className="flex items-center justify-between">
                            <span className="text-purple-300 flex items-center text-xs">
                              <Target className="w-3 h-3 mr-1" />
                              Consensus Needed
                            </span>
                            <span className="text-purple-300 text-xs font-bold">{project.pending_votes} votes</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </button>
            ))}
          </div>

          {/* Enhanced Empty State */}
          {projects.length === 0 && (
            <div className="text-center py-16">
              <div className="bg-black/40 backdrop-blur-xl rounded-3xl p-12 border border-cyan-400/30 shadow-2xl max-w-2xl mx-auto">
                <Target className="w-20 h-20 mx-auto mb-6 text-cyan-400/60" />
                <h3 
                  className="text-3xl font-bold text-white mb-4"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Welcome to Project Salons
                </h3>
                <p 
                  className="text-white/80 text-lg mb-6 leading-relaxed"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Transform everyday challenges into consciousness-driven collaborative experiences. 
                  Whether it's buying mom a car, organizing community projects, or personal growth - 
                  let's avoid the debacles and create belonging while getting things done.
                </p>
                <Button
                  onClick={() => alert('Create project coming soon...')}
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-8 py-4 text-lg"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create Your First Project Salon
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile App-Style Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-gray-900/95 backdrop-blur-xl border-t border-gray-700">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center justify-around">
              
              {/* Projects Overview */}
              <button
                onClick={() => setActiveView('overview')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeView === 'overview' ? 'text-cyan-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <Target className="w-5 h-5" />
                <span className="text-xs font-medium">Projects</span>
              </button>
              
              {/* Messages */}
              <button
                onClick={() => setActiveView('messages')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeView === 'messages' ? 'text-purple-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-xs font-medium">Messages</span>
              </button>
              
              {/* To Do */}
              <button
                onClick={() => setActiveView('todo')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeView === 'todo' ? 'text-green-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <CheckSquare className="w-5 h-5" />
                <span className="text-xs font-medium">To Do</span>
              </button>
              
              {/* Calendar */}
              <button
                onClick={() => setActiveView('calendar')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeView === 'calendar' ? 'text-blue-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <Calendar className="w-5 h-5" />
                <span className="text-xs font-medium">Calendar</span>
              </button>
              
              {/* Documents/Links */}
              <button
                onClick={() => setActiveView('docs')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeView === 'docs' ? 'text-orange-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <FileText className="w-5 h-5" />
                <span className="text-xs font-medium">Docs</span>
              </button>
              
              {/* Peeps (Team Members) */}
              <button
                onClick={() => setActiveView('peeps')}
                className={`flex flex-col items-center space-y-1 transition-colors ${
                  activeView === 'peeps' ? 'text-pink-400' : 'text-white/70 hover:text-white'
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="text-xs font-medium">Peeps</span>
              </button>
              
              {/* Back to Game */}
              <button
                onClick={onNavigateToGame}
                className="flex flex-col items-center space-y-1 text-white/70 hover:text-white transition-colors"
              >
                <Home className="w-5 h-5" />
                <span className="text-xs font-medium">Game</span>
              </button>
            </div>
          </div>
        </div>
        
        <UniversalHeartToggle />
        <PersistentFooter />
      </div>
    );
  }

  // Individual project view - Enhanced Project Salon Interface
  return (
    <div 
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/ewjnynd2_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2003_30_50%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/30 to-black/70"></div>

      <div className="relative z-10 max-w-6xl mx-auto p-6 pb-24">
        
        {/* Enhanced Project Header */}
        <div className="bg-black/40 backdrop-blur-xl border border-white/20 rounded-2xl p-6 mb-6 shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => setSelectedProject(null)}
                variant="outline"
                size="sm"
                className="text-white border-white/40"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Projects
              </Button>
              
              <div>
                <h1 
                  className="text-2xl font-bold text-white"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  {selectedProject.name}
                </h1>
                <div className="flex items-center space-x-3 mt-1">
                  <Badge className={`text-xs ${getHealthColor(selectedProject.health_status)}`}>
                    {getHealthIcon(selectedProject.health_status)}
                    <span className="ml-1 capitalize">{selectedProject.health_status.replace('_', ' ')}</span>
                  </Badge>
                  <span className="text-white/70 text-sm">{selectedProject.members} members</span>
                  <span className="text-cyan-300 text-sm">{selectedProject.progress}% complete</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setShowSettings(!showSettings)}
                variant="outline"
                size="sm"
                className="text-cyan-300 border-cyan-400"
              >
                <Settings className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={() => alert('M.ai.ry project assistant coming soon...')}
                className="bg-purple-600/30 border border-purple-400/50 text-purple-300"
                size="sm"
              >
                <Brain className="w-4 h-4 mr-1" />
                M.ai.ry
              </Button>
            </div>
          </div>
          
          {/* Project Settings Panel */}
          {showSettings && (
            <div className="mt-6 p-4 bg-black/40 rounded-xl border border-white/10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button
                  onClick={() => alert('Archive project feature coming soon...')}
                  variant="outline"
                  size="sm"
                  className="text-yellow-300 border-yellow-400"
                >
                  <Archive className="w-3 h-3 mr-1" />
                  Archive
                </Button>
                
                <Button
                  onClick={() => alert('Privacy settings coming soon...')}
                  variant="outline"
                  size="sm"
                  className="text-blue-300 border-blue-400"
                >
                  <Eye className="w-3 h-3 mr-1" />
                  Privacy
                </Button>
                
                <Button
                  onClick={() => alert('Add members coming soon...')}
                  variant="outline"
                  size="sm"
                  className="text-green-300 border-green-400"
                >
                  <Users className="w-3 h-3 mr-1" />
                  Add Members
                </Button>
                
                <Button
                  onClick={() => alert('Delete project - confirm first')}
                  variant="outline"
                  size="sm"
                  className="text-red-300 border-red-400"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Dynamic Content Area - Full 5-Tab Implementation */}
        <div className="bg-black/30 backdrop-blur-xl rounded-2xl border border-white/20 p-6 mb-6 min-h-96">
          
          {/* Messages Tab - Enhanced with M.ai.ry Integration */}
          {activeView === 'messages' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white text-lg font-bold">Project Messages</h3>
                <Button
                  onClick={() => alert('M.ai.ry project guidance coming soon...')}
                  className="bg-purple-600/30 border border-purple-400/50 text-purple-300"
                  size="sm"
                >
                  <Brain className="w-3 h-3 mr-1" />
                  Ask M.ai.ry
                </Button>
              </div>
              
              {/* Messages Display Area */}
              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto bg-black/20 rounded-xl p-4">
                {projectMessages.map((msg, index) => (
                  <div key={msg.id || index} className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {msg.sender[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-white font-medium text-sm">{msg.sender}</span>
                        <span className="text-white/50 text-xs">{msg.timestamp.toLocaleTimeString()}</span>
                      </div>
                      <p className="text-white/90 text-sm break-words">{msg.content}</p>
                      
                      {/* Enhanced Todo Conversion with Apple Reminders Style */}
                      {msg.convert_to_todo && (
                        <div className="mt-3 p-3 bg-green-500/20 rounded-lg border border-green-400/30">
                          <p className="text-green-200 text-xs mb-2">💡 Convert to task?</p>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => convertMessageToTodo(msg.original_message)}
                              size="sm"
                              className="bg-green-600/50 text-green-200 border border-green-400/50 flex-1"
                            >
                              <CheckSquare className="w-3 h-3 mr-1" />
                              Add to Tasks
                            </Button>
                            <Button
                              onClick={() => setProjectMessages(prev => prev.filter(m => m.id !== msg.id))}
                              variant="outline"
                              size="sm"
                              className="text-white/50 border-white/20"
                            >
                              No Thanks
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {projectMessages.length === 0 && (
                  <div className="text-center py-8 text-white/60">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                      Start the conversation! Share updates, ideas, or ask questions.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Enhanced Message Input with Smart Detection */}
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendProjectMessage()}
                  placeholder="Share updates, ask questions, or describe tasks..."
                  className="flex-1 p-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/60"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                />
                <Button
                  onClick={sendProjectMessage}
                  disabled={!currentMessage.trim()}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="text-center">
                <p className="text-white/50 text-xs" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                  M.ai.ry automatically detects tasks and offers to add them to your todo list
                </p>
              </div>
            </div>
          )}
          
          {/* To Do Tab - Apple Reminders Style */}
          {activeView === 'todo' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white text-lg font-bold">Project Tasks</h3>
                <div className="flex items-center space-x-2">
                  <span className="text-white/60 text-sm">
                    {todos.filter(t => !t.completed).length} of {todos.length} remaining
                  </span>
                  <Button
                    onClick={() => alert('M.ai.ry task guidance coming soon...')}
                    className="bg-purple-600/30 border border-purple-400/50 text-purple-300"
                    size="sm"
                  >
                    <Brain className="w-3 h-3 mr-1" />
                    M.ai.ry
                  </Button>
                </div>
              </div>
              
              {/* Apple Reminders Style Quick Add */}
              <div className="bg-green-500/10 rounded-xl p-4 border border-green-400/30">
                <div className="flex space-x-3">
                  <Plus className="w-5 h-5 text-green-400 mt-1 shrink-0" />
                  <input
                    type="text"
                    value={newTodoText}
                    onChange={(e) => setNewTodoText(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newTodoText.trim()) {
                        convertMessageToTodo(newTodoText);
                        setNewTodoText('');
                      }
                    }}
                    placeholder="New Task"
                    className="flex-1 p-2 bg-transparent border-none text-white placeholder-green-300/60 focus:outline-none text-base"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  />
                  <Button
                    onClick={() => {
                      if (newTodoText.trim()) {
                        convertMessageToTodo(newTodoText);
                        setNewTodoText('');
                      }
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white"
                    size="sm"
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              {/* Apple Reminders Style Todo List */}
              <div className="space-y-1">
                {todos
                  .sort((a, b) => {
                    // Sort by priority (1=highest), then by completion status
                    if (a.completed !== b.completed) return a.completed ? 1 : -1;
                    return (a.priority || 4) - (b.priority || 4);
                  })
                  .map((todo) => (
                  <div key={todo.id} className="group">
                    <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 border border-white/10 hover:border-white/20 transition-all">
                      <div className="flex items-center space-x-3">
                        {/* Apple-style checkbox */}
                        <button
                          onClick={() => {
                            setTodos(prev => prev.map(t => 
                              t.id === todo.id ? {...t, completed: !t.completed} : t
                            ));
                          }}
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                            todo.completed 
                              ? 'bg-green-500 border-green-500' 
                              : 'border-white/40 hover:border-white/60'
                          }`}
                        >
                          {todo.completed && <CheckCircle className="w-3 h-3 text-white" />}
                        </button>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className={`text-white text-sm ${todo.completed ? 'line-through opacity-60' : ''}`}>
                              {todo.text}
                            </span>
                            
                            {/* Priority indicators */}
                            {todo.priority === 1 && <Badge className="bg-red-500/30 text-red-300 text-xs">High</Badge>}
                            {todo.priority === 2 && <Badge className="bg-yellow-500/30 text-yellow-300 text-xs">Medium</Badge>}
                            
                            {todo.created_from_message && (
                              <Badge className="bg-purple-500/30 text-purple-200 text-xs">
                                Smart Created
                              </Badge>
                            )}
                          </div>
                          
                          {todo.due_date && (
                            <div className="flex items-center space-x-1 mt-1">
                              <Calendar className="w-3 h-3 text-white/50" />
                              <span className="text-white/50 text-xs">
                                Due {new Date(todo.due_date).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Quick actions on hover */}
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                          <Button
                            onClick={() => alert('Edit task coming soon...')}
                            variant="outline"
                            size="sm"
                            className="text-white/50 border-white/20 p-1"
                          >
                            <Settings className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {todos.length === 0 && (
                  <div className="text-center py-8 text-white/60">
                    <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-40" />
                    <p style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                      No tasks yet. Add one above or convert from messages!
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Calendar Tab - Enhanced with Your Image Layout */}
          {activeView === 'calendar' && (
            <ProjectCalendar 
              project={selectedProject}
              onBack={() => setActiveView('overview')}
            />
          )}
          
          {/* Documents Tab - Enhanced Placeholder */}
          {activeView === 'docs' && (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 mx-auto mb-4 text-orange-400/60" />
              <h3 className="text-white text-xl font-bold mb-2" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                Documents & Links
              </h3>
              <p className="text-white/70 mb-4">Shared resources and project documentation</p>
              <div className="bg-orange-500/20 rounded-lg p-4 border border-orange-400/30 max-w-md mx-auto">
                <p className="text-orange-200 text-sm">
                  📄 Coming Soon: File sharing, link management, and collaborative documentation
                </p>
              </div>
            </div>
          )}
          
          {/* Peeps Tab - Enhanced Team Management */}
          {activeView === 'peeps' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white text-lg font-bold">Team Members</h3>
                <Button
                  onClick={() => alert('Add team members coming soon...')}
                  className="bg-pink-600/30 border border-pink-400/50 text-pink-300"
                  size="sm"
                >
                  <Users className="w-3 h-3 mr-1" />
                  Invite
                </Button>
              </div>
              
              {/* Team Members Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Current User */}
                <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                  <div className="flex items-center space-x-3">
                    <img 
                      src={player.avatar_url}
                      alt={player.name}
                      className="w-10 h-10 rounded-full border-2 border-cyan-400/50"
                    />
                    <div>
                      <p className="text-white font-medium">{player.name}</p>
                      <div className="flex items-center space-x-2">
                        <Badge className="bg-cyan-500/30 text-cyan-200 text-xs">Project Owner</Badge>
                        <span className="text-white/60 text-xs">Online</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Mock team members for demo */}
                {selectedProject.members > 1 && [...Array(selectedProject.members - 1)].map((_, i) => (
                  <div key={i} className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold">
                        {['A', 'B', 'C', 'D'][i]}
                      </div>
                      <div>
                        <p className="text-white font-medium">Team Member {i + 1}</p>
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-purple-500/30 text-purple-200 text-xs">Collaborator</Badge>
                          <span className="text-white/40 text-xs">2h ago</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-8">
                <Button
                  onClick={() => alert('Team collaboration features coming soon...')}
                  variant="outline"
                  className="text-pink-300 border-pink-400/50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Invite More Peeps
                </Button>
              </div>
            </div>
          )}
          
          {/* Default overview when no specific tab selected */}
          {activeView === 'overview' && (
            <div className="text-center py-16">
              <Target className="w-16 h-16 mx-auto mb-4 text-cyan-400/60" />
              <h3 
                className="text-2xl font-bold text-white mb-4"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                {selectedProject.name}
              </h3>
              <p 
                className="text-white/70 text-lg mb-6"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Use the tabs below to manage your project salon
              </p>
              
              {/* Project Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="bg-purple-500/20 rounded-lg p-4 border border-purple-400/30">
                  <MessageCircle className="w-6 h-6 mx-auto mb-2 text-purple-300" />
                  <p className="text-purple-200 text-sm font-medium">Messages</p>
                  <p className="text-white text-lg font-bold">{projectMessages.length}</p>
                </div>
                
                <div className="bg-green-500/20 rounded-lg p-4 border border-green-400/30">
                  <CheckSquare className="w-6 h-6 mx-auto mb-2 text-green-300" />
                  <p className="text-green-200 text-sm font-medium">Tasks</p>
                  <p className="text-white text-lg font-bold">{todos.length}</p>
                </div>
                
                <div className="bg-blue-500/20 rounded-lg p-4 border border-blue-400/30">
                  <Users className="w-6 h-6 mx-auto mb-2 text-blue-300" />
                  <p className="text-blue-200 text-sm font-medium">Team</p>
                  <p className="text-white text-lg font-bold">{selectedProject.members}</p>
                </div>
                
                <div className="bg-orange-500/20 rounded-lg p-4 border border-orange-400/30">
                  <Target className="w-6 h-6 mx-auto mb-2 text-orange-300" />
                  <p className="text-orange-200 text-sm font-medium">Progress</p>
                  <p className="text-white text-lg font-bold">{selectedProject.progress}%</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Same Mobile Navigation for Project View */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-gray-900/95 backdrop-blur-xl border-t border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-around">
            
            {/* Messages */}
            <button
              onClick={() => setActiveView('messages')}
              className={`flex flex-col items-center space-y-1 transition-colors ${
                activeView === 'messages' ? 'text-purple-400' : 'text-white/70 hover:text-white'
              }`}
            >
              <MessageCircle className="w-5 h-5" />
              <span className="text-xs font-medium">Messages</span>
            </button>
            
            {/* To Do */}
            <button
              onClick={() => setActiveView('todo')}
              className={`flex flex-col items-center space-y-1 transition-colors ${
                activeView === 'todo' ? 'text-green-400' : 'text-white/70 hover:text-white'
              }`}
            >
              <CheckSquare className="w-5 h-5" />
              <span className="text-xs font-medium">To Do</span>
            </button>
            
            {/* Calendar */}
            <button
              onClick={() => setActiveView('calendar')}
              className={`flex flex-col items-center space-y-1 transition-colors ${
                activeView === 'calendar' ? 'text-blue-400' : 'text-white/70 hover:text-white'
              }`}
            >
              <Calendar className="w-5 h-5" />
              <span className="text-xs font-medium">Calendar</span>
            </button>
            
            {/* Documents/Links */}
            <button
              onClick={() => setActiveView('docs')}
              className={`flex flex-col items-center space-y-1 transition-colors ${
                activeView === 'docs' ? 'text-orange-400' : 'text-white/70 hover:text-white'
              }`}
            >
              <FileText className="w-5 h-5" />
              <span className="text-xs font-medium">Docs</span>
            </button>
            
            {/* Peeps */}
            <button
              onClick={() => setActiveView('peeps')}
              className={`flex flex-col items-center space-y-1 transition-colors ${
                activeView === 'peeps' ? 'text-pink-400' : 'text-white/70 hover:text-white'
              }`}
            >
              <Users className="w-5 h-5" />
              <span className="text-xs font-medium">Peeps</span>
            </button>
          </div>
        </div>
      </div>
      
      <UniversalHeartToggle />
      <PersistentFooter />
    </div>
  );
};

export default MyProjectsPage;